import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

/** Safety net: the overlay is closed by itself after this long. */
export const LOADER_TIMEOUT_MS = 30_000;

export interface LoaderShowOptions {
  /** Short text shown under the animation (e.g. "Guardando venta..."). */
  message?: string;
  /**
   * The operation moves money or stock. On timeout the copy warns that it may
   * have completed, so the user checks before retrying.
   */
  money?: boolean;
}

export interface LoaderTimeoutEvent {
  money: boolean;
}

/** Opaque id returned by `show` so `hide` releases the right entry. */
export type LoaderToken = number;

interface LoaderEntry extends LoaderShowOptions {
  token: LoaderToken;
}

@Injectable({ providedIn: 'root' })
export class LoaderService {
  private nextToken = 1;
  private entries: LoaderEntry[] = [];
  private timeoutId: ReturnType<typeof setTimeout> | null = null;

  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly messageSubject = new BehaviorSubject<string | null>(null);
  private readonly timedOutSubject = new Subject<LoaderTimeoutEvent>();

  /** Emits only on the 0 -> 1 and 1 -> 0 transitions of active requests. */
  public readonly isLoading$: Observable<boolean> =
    this.loadingSubject.asObservable();
  /** Message of the oldest active request that has one, or `null`. */
  public readonly message$: Observable<string | null> =
    this.messageSubject.asObservable();
  /**
   * Emits when requests stayed active for `LOADER_TIMEOUT_MS`. The overlay is
   * released (`isLoading$` -> false) but the requests are NOT cancelled: they
   * may still complete on the backend.
   */
  public readonly timedOut$: Observable<LoaderTimeoutEvent> =
    this.timedOutSubject.asObservable();

  show(options: LoaderShowOptions = {}): LoaderToken {
    const token = this.nextToken++;
    this.entries.push({ token, ...options });
    this.publishMessage();

    if (this.entries.length === 1) {
      this.startTimeout();
      this.loadingSubject.next(true);
    }

    return token;
  }

  /**
   * Releases one active request. With a token it releases that exact entry
   * (ignored if it was already dropped by timeout/reset); without it, the
   * oldest one.
   */
  hide(token?: LoaderToken): void {
    if (this.entries.length === 0) {
      return;
    }

    const index =
      token === undefined
        ? 0
        : this.entries.findIndex((entry) => entry.token === token);
    if (index === -1) {
      return;
    }

    this.entries.splice(index, 1);
    this.publishMessage();

    if (this.entries.length === 0) {
      this.clearTimeout();
      this.loadingSubject.next(false);
    }
  }

  reset(): void {
    this.entries = [];
    this.clearTimeout();
    this.publishMessage();
    this.loadingSubject.next(false);
  }

  private publishMessage(): void {
    const message =
      this.entries.find((entry) => !!entry.message)?.message ?? null;
    if (message !== this.messageSubject.value) {
      this.messageSubject.next(message);
    }
  }

  private startTimeout(): void {
    this.clearTimeout();
    this.timeoutId = setTimeout(() => this.handleTimeout(), LOADER_TIMEOUT_MS);
  }

  private clearTimeout(): void {
    if (this.timeoutId !== null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }
  }

  private handleTimeout(): void {
    this.timeoutId = null;
    if (this.entries.length === 0) {
      return;
    }

    const money = this.entries.some((entry) => entry.money === true);
    // Drop the entries without cancelling anything: a late `hide(token)` is ignored.
    this.entries = [];
    this.publishMessage();
    this.loadingSubject.next(false);
    this.timedOutSubject.next({ money });
  }
}
